import { CustomerParamsService } from "@/services/network/customer-params";
import { LocationMasterParams } from "@/services/network/type";
import {
  DefaultError,
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";

const customerParamsService = new CustomerParamsService();

interface State {
  id: number;
  name: string;
}

interface City {
  id: number;
  name: string;
  stateId: number;
}

interface Area {
  id: number;
  name: string;
  districtId: number;
}

interface Pincode {
  id: number;
  pincode: string;
  areaId: number;
}

export type LocationDataItem = State | City | Area | Pincode;

interface LocationDataPage<T extends LocationDataItem> {
  total: number;
  page: number;
  totalPages: number;
  data: T[];
}

interface ApiServiceResponse<T extends LocationDataItem> {
  status: number;
  message: string;
  data: LocationDataPage<T>;
}

const useLocationMaster = <T extends LocationDataItem>(
  params: LocationMasterParams,
  options?: { enabled?: boolean; queryKeySuffix?: string | number }
) => {
  const {
    data,
    error,
    isError,
    isLoading,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery<
    ApiServiceResponse<T>,
    DefaultError,
    InfiniteData<ApiServiceResponse<T>>,
    QueryKey,
    number
  >({
    queryKey: [
      CustomerParamsService.QUERY_KEY.LOCATION_MASTER,
      params.type,
      ...(params.parent_id ? params.parent_id.sort().join(",") : ["NO_PARENT"]),
      params.search,
      options?.queryKeySuffix,
    ].filter((key) => key !== undefined && key !== null && key !== ""),
    queryFn: async ({ pageParam = 1 }) => {
      const apiParams = { ...params, page: pageParam };
      const response = await customerParamsService.getLocationMaster(apiParams);
      if (response.status === 200 && response.data) {
        return response as ApiServiceResponse<T>;
      } else {
        throw new Error(response.message || "Failed to fetch location master");
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.data.page < lastPage.data.totalPages) {
        return lastPage.data.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled:
      options?.enabled !== undefined
        ? options.enabled
        : params.type === "state" ||
          (params.type && params.parent_id && params.parent_id.length > 0) ||
          !params.type,
  });

  const transformedData: T[] | undefined = data?.pages.flatMap(
    (page) => page.data.data
  );

  return {
    data: transformedData,
    error,
    isError,
    isLoading,
    status,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
  };
};

export { useLocationMaster };
export type { Area, City, Pincode, State };
